import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { notEmpty } from "@orb-labs/types";
import { capture } from "@orb-labs/orby-react";
import { persistCampaignAttribution } from "@/observability";

/**
 * Outreach short-link redirect (`/p/:code`).
 *
 * The Xreacher cold-DM campaign sends clean short links (e.g. `bullrun.app/p/ab`)
 * instead of long `?utm_*=` query strings — long UTM URLs read as spam in cold
 * DMs and hurt deliverability. Each short code maps to a campaign UTM
 * (`utm_content` carries segment + template set), which we persist through the
 * same attribution path as query-string UTMs (`persistCampaignAttribution`), so
 * a later signup attributes correctly. Then we replace the URL with `/` so the
 * user lands on the normal home surface.
 *
 * Lives above the desktop/mobile router split (called in `AppInner`) so the
 * links resolve on both viewports. Mapping is documented in
 * `docs/growth/xreacher-campaign-spec.md` (Tracking links).
 */

const CAMPAIGN_BASE = {
  utm_source: "x",
  utm_medium: "dm",
  utm_campaign: "predictions_wedge",
} as const;

// Short code → utm_content. `v1` = personalized template set 1,
// `beta` = early-beta feedback template set 2. A/B/C = seed segment.
const PROMO_CODE_CONTENT: Record<string, string> = {
  a: "segA_v1",
  b: "segB_v1",
  c: "segC_v1",
  ab: "segA_beta",
  bb: "segB_beta",
  cb: "segC_beta",
};

const PROMO_PATH = /^\/p\/([A-Za-z0-9_-]+)\/?$/;

export function usePromoOutreachRedirect(): void {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Guard on the handled code, not a mount-once boolean: AppInner lives for the
  // whole session, so a boolean would skip attribution for a second promo link
  // opened in the same tab (e.g. /p/ab then /p/bb).
  const handledRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const match = PROMO_PATH.exec(pathname);
    if (!match) return;
    const [, code] = match;
    if (handledRef.current === code) return;
    handledRef.current = code;

    const utmContent = PROMO_CODE_CONTENT[code];
    const recognized = notEmpty(utmContent);

    // Only attribute recognized codes; an unrecognized code still redirects
    // home but is logged (recognized:false) so malformed links are visible
    // rather than silently mis-attributed.
    if (recognized) {
      persistCampaignAttribution({ ...CAMPAIGN_BASE, utm_content: utmContent });
    }
    capture("promo_link_click", { code, recognized });

    navigate("/", { replace: true });
  }, [pathname, navigate]);
}
